import { createTaxClient } from "@tcg/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useClientOptions } from "@/lib/api/use-api";

import { type FeatureFlag, type FeatureFlags, parseFlags, PHASE1_FLAGS } from "./flags";

export const FEATURE_FLAGS_PATH = "/v1/admin/settings/features";
export const FEATURE_FLAGS_KEY = ["shell", "feature-flags"] as const;

/**
 * Feature flags for the admin (T11-09): tax-service `GET /v1/admin/settings/features` (T01-21 —
 * any signed-in staff; the flags come from the environment, so they change only with a redeploy).
 * Every screen hides or shows its "coming soon" cards with them. While the answer is on its way, or
 * when it cannot be read, every flag counts as OFF (phase 1, R18) — a screen never flashes an
 * integration that is not switched on.
 */
export function useFeatureFlagsQuery(): UseQueryResult<FeatureFlags> {
  const options = useClientOptions();
  return useQuery({
    queryKey: FEATURE_FLAGS_KEY,
    meta: { silent: true },
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<FeatureFlags> => {
      const { data, error } = await createTaxClient(options).GET(FEATURE_FLAGS_PATH, { signal });
      if (error !== undefined || data === undefined) {
        throw new Error("feature flags unavailable");
      }
      return parseFlags(data);
    },
  });
}

export function useFeatureFlags(): FeatureFlags {
  return useFeatureFlagsQuery().data ?? PHASE1_FLAGS;
}

export function useFeature(flag: FeatureFlag): boolean {
  return useFeatureFlags()[flag];
}
